import React, { useMemo, useRef, useState } from "react";
import { DateInputSelectProps, ListItem } from "./DateInputSelect.types";
import {
  daysListDefault,
  monthNamesDefault,
  updateDaysListWithDisabledDates,
  updateMonthListValuesWithDisabledItems,
  updateYearListValuesWithDisabledItems,
  yearsList as yearsListDefault,
} from "./util/DateInputSelect.util";
import SelectInput from "../../select-input";
import { getMonthLength } from "../calendar/calendar.util";
import { NOOP } from "../date-input-field/util/DateInputField.util";

export const DateInputSelect = React.forwardRef<
  HTMLInputElement,
  DateInputSelectProps
>(
  (
    {
      monthLabel = "Month",
      yearLabel = "Year",
      dayLabel = "Day",
      label,
      monthsList = monthNamesDefault,
      daysList: daysListProp = daysListDefault,
      yearsList = yearsListDefault,
      monthSelectFieldName,
      daySelectFieldName,
      yearSelectFieldName,
      monthValue: monthValueProp,
      yearValue: yearValueProp,
      dayValue: dayValueProp,
      defaultMonthValue,
      defaultDayValue,
      defaultYearValue,
      disabled,
      className,
      dateItemsDisabled,
      onChange,
    },
    ref: any
  ): React.ReactElement => {
    const [monthValueState, setMonthValueState] = useState<string>(
      monthValueProp || defaultMonthValue || ""
    );
    const [dayValueState, setDayValueState] = useState<string>(
      dayValueProp || defaultDayValue || ""
    );
    const [yearValueState, setYearValueState] = useState<string>(
      yearValueProp || defaultYearValue || ""
    );

    const { current: isControlled } = useRef(
      monthValueProp !== undefined ||
        yearValueProp !== undefined ||
        dayValueProp !== undefined
    );

    const monthValue = isControlled ? monthValueProp || "" : monthValueState;
    const yearValue = isControlled ? yearValueProp || "" : yearValueState;
    const dayValue = isControlled ? dayValueProp || "" : dayValueState;

    const [maxDaysInMonth, setMaxDaysInMonth] = useState<string>((): string =>
      getMonthLength(parseInt(monthValue), Number(yearValue)).toString()
    );

    const monthListWithDisabledDates = useMemo((): ListItem[] => {
      return updateMonthListValuesWithDisabledItems(
        monthsList || [],
        dateItemsDisabled
      );
    }, [dateItemsDisabled, monthsList]);

    const yearListWithDisabledDates = useMemo((): ListItem[] => {
      return updateYearListValuesWithDisabledItems(
        yearsList || [],
        dateItemsDisabled
      );
    }, [dateItemsDisabled, yearsList]);

    const dayListWithDisabledDates = useMemo((): ListItem[] => {
      return updateDaysListWithDisabledDates(
        daysListProp || [],
        maxDaysInMonth,
        dateItemsDisabled
      );
    }, [dateItemsDisabled, daysListProp, maxDaysInMonth]);

    const handleChange = (
      value: string,
      source: "year" | "month" | "day"
    ): void => {
      const _yearValue = source === "year" ? value : yearValue;
      const _monthValue = source === "month" ? value : monthValue;
      const _dayValue = source === "day" ? value : dayValue;

      const _maxDaysInMonth = getMonthLength(
        parseInt(_monthValue),
        Number(_yearValue)
      ).toString();

      const newDayValue =
        _dayValue && Number(_dayValue) >= Number(_maxDaysInMonth)
          ? _maxDaysInMonth
          : _dayValue;

      setMaxDaysInMonth(_maxDaysInMonth);
      setDayValueState(newDayValue);
      setYearValueState(_yearValue);
      setMonthValueState(_monthValue);

      onChange?.({
        day: newDayValue,
        month: _monthValue,
        year: _yearValue,
      });
    };

    return (
      <fieldset className={`${className} spark-date spark-date--select`}>
        <span className="spark-select-group">
          <SelectInput
            disabled={disabled}
            label={monthLabel}
            name={monthSelectFieldName}
            options={monthListWithDisabledDates}
            value={monthValue?.toString()}
            onChange={(e, value): void => handleChange(value, "month")}
          />
          <SelectInput
            value={dayValue?.toString()}
            disabled={disabled}
            label={dayLabel}
            name={daySelectFieldName}
            options={dayListWithDisabledDates}
            onChange={(e, value): void => handleChange(value, "day")}
          />
          <SelectInput
            value={yearValue?.toString()}
            disabled={disabled}
            label={yearLabel}
            name={yearSelectFieldName}
            options={yearListWithDisabledDates}
            onChange={(e, value): void => handleChange(value, "year")}
          />
          <span className="spark-label">{label}</span>
        </span>
        <input
          className="spark-input__field"
          type="date"
          ref={ref}
          onChange={NOOP}
          value={`${yearValue}${
            monthValue ? "-" + monthValue?.padStart(2, "0") : ""
          }${dayValue ? "-" + dayValue?.padStart(2, "0") : ""}`}
          style={{ display: "none" }}
        />
      </fieldset>
    );
  }
);

DateInputSelect.displayName = "DateInputSelect";
